import React, { useMemo } from "react";
import { NavDropdown } from "react-bootstrap";
import { useAdminHoverPrefetch } from "@/hooks/useHoverPrefetch";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { isSameRouteNavigation } from "@/components/navigation/isSameRouteNavigation";

interface PrefetchNavDropdownItemProps {
    href: string;
    active?: boolean;
    target?: string;
    rel?: string;
    children: React.ReactNode;
    className?: string;
}

/**
 * Enhanced NavDropdown.Item with hover prefetching for admin pages
 * Automatically prefetches admin routes on hover for improved performance
 * Works seamlessly with React Bootstrap's native dropdown closing mechanism
 */
export const PrefetchNavDropdownItem: React.FC<
    PrefetchNavDropdownItemProps
> = ({ href, active, target, rel, children, className, ...props }) => {
    // const { userProfile, externalUserProfile } = useAuth();
    // const currentUserId =
    //     userProfile?.userId ?? externalUserProfile?.userId ?? null;
    // Append userId to the href (keeps existing query params intact)
    const hrefWithUserId = useMemo(() => {
        return href;
        // if (!currentUserId || href.startsWith("http")) return href;
        // const sep = href.includes("?") ? "&" : "?";
        // return `${href}${sep}userId=${encodeURIComponent(currentUserId)}`;
    }, [href]);

    const { onMouseEnter, onMouseLeave } =
        useAdminHoverPrefetch(hrefWithUserId);
    const router = useRouter();
    const queryClient = useQueryClient();

    const handleClick = (e: React.MouseEvent) => {
        // Don't intercept external links or links with target="_blank"
        if (target === "_blank" || href.startsWith("http")) {
            return; // Let browser handle normally
        }

        // Prevent full page reload
        e.preventDefault();

        if (isSameRouteNavigation(hrefWithUserId, window.location.href)) {
            return;
        }

        // Remove cache BEFORE navigation to force fresh fetch
        const basePath = href.split("?")[0];
        if (basePath === "/request/manage") {
            queryClient.removeQueries({ queryKey: ["requestList"] });
        } else if (basePath === "/trip/manage") {
            queryClient.removeQueries({ queryKey: ["tripList"] });
        }

        // Navigate using Next.js router
        // Using setTimeout to ensure the dropdown close animation/event has a chance to start
        setTimeout(() => {
            router.push(hrefWithUserId);
        }, 0);
    };

    return (
        <NavDropdown.Item
            href={hrefWithUserId}
            active={active}
            target={target}
            rel={rel}
            className={className}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={handleClick}
            {...props}
        >
            {children}
        </NavDropdown.Item>
    );
};
